import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../components/EmptyState';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { PageHeader } from '../../components/PageHeader';
import { RichTextEditor } from '../../components/RichTextEditor';
import { Tabs } from '../../components/Tabs';
import { formatDateTime } from '../../i18n';
import { useMasterData } from '../masterdata/queries';
import { OutboundPanel } from './OutboundPanel';
import { TemplatesPanel } from './TemplatesPanel';
import {
  useCreateMailAccount,
  useCreateMailFilter,
  useCreateSignature,
  useDeleteMailAccount,
  useDeleteMailFilter,
  useDeleteSignature,
  useMailAccounts,
  useMailFilters,
  usePollMailAccounts,
  useUpdateMailAccount,
  useSignatures,
} from './queries';
import { Select } from '../../components/Select';

/**
 * Die Mailverwaltung -- F3-01, F3-04, F3-06.
 *
 * Postfaecher, Filter und Signaturen in einem Zweig: Wer ein Postfach
 * einrichtet, richtet fast immer im selben Zug einen Filter und eine
 * Signatur dazu ein. Drei Menuepunkte waeren drei Wege fuer einen Vorgang.
 *
 * **Der Abrufknopf ist kein Beiwerk.** Der Taktgeber holt jede Minute ab --
 * aber wer gerade ein Postfach angelegt hat, will jetzt wissen, ob die
 * Zugangsdaten stimmen, und nicht in sechzig Sekunden. Der letzte Fehler
 * steht am Konto, damit die Antwort auch dort zu lesen ist.
 */
export function MailAdminPage() {
  const { t } = useTranslation();

  return (
    <>
      <PageHeader title={t('mail.title')} subtitle={t('mail.subtitle')} />

      <Tabs
        ariaLabel={t('mail.title')}
        tabs={[
          { value: 'accounts', label: t('mail.accounts'), content: <AccountsPanel /> },
          { value: 'filters', label: t('mail.filters'), content: <FiltersPanel /> },
          { value: 'signatures', label: t('mail.signatures'), content: <SignaturesPanel /> },
          // Die Vorlage bestimmt, was der Kunde zu lesen bekommt; die
          // Warteschlange, ob es ihn erreicht hat. Beides gehoert zum Kanal
          // und nicht in eine eigene Ecke der Verwaltung (F3-05, F3-07).
          { value: 'templates', label: t('mail.templates'), content: <TemplatesPanel /> },
          { value: 'outbound', label: t('mail.outbound'), content: <OutboundPanel /> },
        ]}
      />
    </>
  );
}

function AccountsPanel() {
  const { t, i18n } = useTranslation();
  const accounts = useMailAccounts();
  const queues = useMasterData('queue');
  const create = useCreateMailAccount();
  const remove = useDeleteMailAccount();
  const poll = usePollMailAccounts();
  const update = useUpdateMailAccount();

  // Nur das Kennwort laesst sich hier nachtragen -- das ist der Fall, der im
  // Alltag vorkommt: Der Mailanbieter erzwingt einen Wechsel, und ohne diese
  // Zeile hiesse das Postfach loeschen und neu einrichten, samt Filtern und
  // Warteschlangenzuordnung.
  const [newPassword, setNewPassword] = useState<Record<string, string>>({});

  const [values, setValues] = useState({
    name: '',
    queue_id: '',
    host: '',
    port: '993',
    username: '',
    password: '',
    folder: 'INBOX',
    use_ssl: true,
    keep_on_server: true,
  });

  const set = <K extends keyof typeof values>(field: K, value: (typeof values)[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  return (
    <div className="stack">
      <section className="card card--flush">
        {accounts.isPending ? <LoadingIndicator /> : null}
        {accounts.isError ? <ErrorNotice error={accounts.error} /> : null}

        {accounts.data?.length === 0 ? (
          <EmptyState icon="mail" title={t('mail.noAccounts')} hint={t('mail.noAccountsHint')} />
        ) : null}

        {accounts.data && accounts.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('mail.host')}</th>
                  <th scope="col">{t('mail.lastPolled')}</th>
                  <th scope="col">{t('tickets.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {accounts.data.map((account) => (
                  <tr key={account.id}>
                    <td>
                      {account.name}{' '}
                      {account.is_active ? null : (
                        <Badge tone="neutral">{t('masterdata.inactive')}</Badge>
                      )}
                    </td>
                    <td className="table__muted">
                      {account.username}@{account.host}:{account.port}
                    </td>
                    <td className="table__muted">
                      {account.last_polled_at
                        ? formatDateTime(account.last_polled_at, i18n.language)
                        : '—'}
                      {/* Der letzte Fehler steht am Konto und nicht in einem
                          Protokoll: Wer die Zugangsdaten eingetragen hat, soll
                          dort nachsehen koennen, wo er sie eingetragen hat. */}
                      {account.last_error ? (
                        <div>
                          <Badge tone="danger">{account.last_error}</Badge>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="row">
                        <input
                          type="password"
                          className="field__control"
                          autoComplete="new-password"
                          aria-label={t('mail.newPasswordFor', { name: account.name })}
                          placeholder={t('mail.newPassword')}
                          value={newPassword[account.id] ?? ''}
                          onChange={(event) => {
                            setNewPassword((current) => ({
                              ...current,
                              [account.id]: event.target.value,
                            }));
                          }}
                        />
                        <Button
                          variant="ghost"
                          disabled={!(newPassword[account.id] ?? '')}
                          busy={update.isPending}
                          onClick={() => {
                            update.mutate(
                              {
                                accountId: account.id,
                                payload: { password: newPassword[account.id] },
                              },
                              {
                                onSuccess: () => {
                                  setNewPassword((current) => ({ ...current, [account.id]: '' }));
                                },
                              },
                            );
                          }}
                        >
                          {t('app.save')}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            remove.mutate(account.id);
                          }}
                        >
                          {t('masterdata.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {remove.isError ? <ErrorNotice error={remove.error} /> : null}
      {update.isError ? <ErrorNotice error={update.error} /> : null}

      <section className="card">
        <div className="row">
          <Button
            variant="secondary"
            busy={poll.isPending}
            onClick={() => {
              poll.mutate();
            }}
          >
            {t('mail.pollNow')}
          </Button>
          <span className="field__hint">{t('mail.pollHint')}</span>
        </div>
        {poll.isError ? <ErrorNotice error={poll.error} /> : null}
      </section>

      <section className="card">
        <h2>{t('mail.newAccount')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="account-name">
            {t('masterdata.name')}
          </label>
          <input
            id="account-name"
            className="field__control"
            value={values.name}
            onChange={(event) => {
              set('name', event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="account-queue">
            {t('tickets.queue')}
          </label>
          <Select
            id="account-queue"
            className="field__control"
            value={values.queue_id}
            onChange={(event) => {
              set('queue_id', event.target.value);
            }}
          >
            <option value="">{t('mail.chooseQueue')}</option>
            {(queues.data ?? []).map((queue) => (
              <option key={queue.id} value={queue.id}>
                {queue.name}
              </option>
            ))}
          </Select>
          <span className="field__hint">{t('mail.queueHint')}</span>
        </div>

        <div className="row">
          <div className="field">
            <label className="field__label" htmlFor="account-host">
              {t('mail.host')}
            </label>
            <input
              id="account-host"
              className="field__control"
              value={values.host}
              onChange={(event) => {
                set('host', event.target.value);
              }}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="account-port">
              {t('mail.port')}
            </label>
            <input
              id="account-port"
              type="number"
              className="field__control"
              value={values.port}
              onChange={(event) => {
                set('port', event.target.value);
              }}
            />
          </div>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="account-username">
            {t('mail.username')}
          </label>
          <input
            id="account-username"
            className="field__control"
            autoComplete="off"
            value={values.username}
            onChange={(event) => {
              set('username', event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="account-password">
            {t('mail.password')}
          </label>
          <input
            id="account-password"
            type="password"
            className="field__control"
            autoComplete="new-password"
            value={values.password}
            onChange={(event) => {
              set('password', event.target.value);
            }}
          />
          <span className="field__hint">{t('mail.passwordHint')}</span>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="account-folder">
            {t('mail.folder')}
          </label>
          <input
            id="account-folder"
            className="field__control"
            value={values.folder}
            onChange={(event) => {
              set('folder', event.target.value);
            }}
          />
        </div>

        <label className="field__checkbox">
          <input
            type="checkbox"
            checked={values.use_ssl}
            onChange={(event) => {
              set('use_ssl', event.target.checked);
            }}
          />
          {t('mail.useSsl')}
        </label>

        <label className="field__checkbox">
          <input
            type="checkbox"
            checked={values.keep_on_server}
            onChange={(event) => {
              set('keep_on_server', event.target.checked);
            }}
          />
          {t('mail.keepOnServer')}
        </label>

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={!values.name.trim() || !values.host.trim() || values.queue_id === ''}
          busy={create.isPending}
          onClick={() => {
            create.mutate(
              {
                name: values.name.trim(),
                queue_id: values.queue_id,
                host: values.host.trim(),
                port: Number(values.port),
                username: values.username.trim(),
                password: values.password,
                folder: values.folder,
                use_ssl: values.use_ssl,
                keep_on_server: values.keep_on_server,
              },
              {
                onSuccess: () => {
                  setValues({
                    name: '',
                    queue_id: '',
                    host: '',
                    port: '993',
                    username: '',
                    password: '',
                    folder: 'INBOX',
                    use_ssl: true,
                    keep_on_server: true,
                  });
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>
    </div>
  );
}

/**
 * Filterregeln auf eingehende Mails -- F3-04, K-42.
 *
 * **Nur eine Bedingung je Filter.** Der Server nimmt beliebig
 * verschachtelte Ausdruecke entgegen; diese Maske bietet die eine Zeile an,
 * die in der Praxis reicht -- "Betreff enthaelt X". Wer mehr braucht, hat
 * einen Fall, den man erst sehen sollte, bevor man eine Oberflaeche dafuer
 * baut.
 */
function FiltersPanel() {
  const { t } = useTranslation();
  const filters = useMailFilters();
  const create = useCreateMailFilter();
  const remove = useDeleteMailFilter();

  const [name, setName] = useState('');
  const [field, setField] = useState('subject');
  const [value, setValue] = useState('');
  const [discard, setDiscard] = useState(false);

  return (
    <div className="stack">
      <section className="card card--flush">
        {filters.isPending ? <LoadingIndicator /> : null}
        {filters.isError ? <ErrorNotice error={filters.error} /> : null}

        {filters.data?.length === 0 ? (
          <EmptyState icon="filter" title={t('mail.noFilters')} hint={t('mail.noFiltersHint')} />
        ) : null}

        {filters.data && filters.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('mail.condition')}</th>
                  <th scope="col">{t('tickets.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filters.data.map((filter) => (
                  <tr key={filter.id}>
                    <td>
                      {filter.name}{' '}
                      {filter.discard ? <Badge tone="warning">{t('mail.discard')}</Badge> : null}
                    </td>
                    <td className="table__muted">{JSON.stringify(filter.match)}</td>
                    <td>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          remove.mutate(filter.id);
                        }}
                      >
                        {t('masterdata.delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="card">
        <h2>{t('mail.newFilter')}</h2>
        <p className="table__muted">{t('mail.filterHint')}</p>

        <div className="field">
          <label className="field__label" htmlFor="filter-name">
            {t('masterdata.name')}
          </label>
          <input
            id="filter-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="row">
          <div className="field">
            <label className="field__label" htmlFor="filter-field">
              {t('mail.field')}
            </label>
            <Select
              id="filter-field"
              className="field__control"
              value={field}
              onChange={(event) => {
                setField(event.target.value);
              }}
            >
              {['subject', 'from', 'to', 'cc', 'body'].map((option) => (
                <option key={option} value={option}>
                  {t(`mailField.${option}`)}
                </option>
              ))}
            </Select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="filter-value">
              {t('mail.contains')}
            </label>
            <input
              id="filter-value"
              className="field__control"
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
              }}
            />
          </div>
        </div>

        <label className="field__checkbox">
          <input
            type="checkbox"
            checked={discard}
            onChange={(event) => {
              setDiscard(event.target.checked);
            }}
          />
          {t('mail.discardHint')}
        </label>

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={!name.trim() || !value.trim()}
          busy={create.isPending}
          onClick={() => {
            create.mutate(
              {
                name: name.trim(),
                match: {
                  operator: 'and',
                  rules: [{ field, op: 'contains', value: value.trim() }],
                },
                discard,
              },
              {
                onSuccess: () => {
                  setName('');
                  setValue('');
                  setDiscard(false);
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>
    </div>
  );
}

function SignaturesPanel() {
  const { t } = useTranslation();
  const signatures = useSignatures();
  const create = useCreateSignature();
  const remove = useDeleteSignature();

  const [name, setName] = useState('');
  const [body, setBody] = useState('');

  return (
    <div className="stack">
      <section className="card card--flush">
        {signatures.isPending ? <LoadingIndicator /> : null}
        {signatures.isError ? <ErrorNotice error={signatures.error} /> : null}

        {signatures.data?.length === 0 ? (
          <EmptyState
            icon="mail"
            title={t('mail.noSignatures')}
            hint={t('mail.noSignaturesHint')}
          />
        ) : null}

        {(signatures.data ?? []).map((signature) => (
          <div className="card__header" key={signature.id}>
            <h2>
              {signature.name}{' '}
              {signature.is_default ? <Badge tone="info">{t('mail.default')}</Badge> : null}
            </h2>
            <Button
              variant="ghost"
              onClick={() => {
                remove.mutate(signature.id);
              }}
            >
              {t('masterdata.delete')}
            </Button>
          </div>
        ))}
      </section>

      <section className="card">
        <h2>{t('mail.newSignature')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="signature-name">
            {t('masterdata.name')}
          </label>
          <input
            id="signature-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="signature-body">
            {t('mail.signatureBody')}
          </label>
          {/* Derselbe Editor wie am Ticket. Ohne Ueberschriften und ohne
              Bild: Eine Signatur ist drei Zeilen unter einer Mail, keine
              Seite -- und ein Bild darin gehoerte in jede ausgehende
              Nachricht als Anhang. */}
          <RichTextEditor
            id="signature-body"
            value={body}
            onChange={setBody}
            ariaLabel={t('mail.signatureBody')}
            features={{ headings: false, image: false, table: false }}
            minRows={4}
          />
        </div>

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={!name.trim()}
          busy={create.isPending}
          onClick={() => {
            create.mutate(
              { name: name.trim(), body_html: body },
              {
                onSuccess: () => {
                  setName('');
                  setBody('');
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>
    </div>
  );
}
